import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';
import {
  IChartDatasetItem,
  IChartWithBars,
  IChartWithStackedBars,
} from 'PDF/components/Charts/types';
import { getColorStyles, getColorType } from 'PDF/helpers/documentStyle';
import { formatStepNumber } from 'PDF/helpers/formatStepNumber';
import { hasDataToRender } from 'PDF/helpers/hasDataToRender';
import { IMagazineTextItem, MagazineTextItemType } from 'PDF/helpers/pdfAlgorithm/types';
import { getMagazineTextObject } from 'PDF/helpers/pdfMapping';
import { IMarketingAndSalesData, ISalesPlanTableProps } from 'PDF/pages/MarketingAndSales/types';

import { IPeriodNames } from 'components/BusinessToolForm/constants/periods';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import {
  colorIndexesByPalleteMonetary,
  colorIndexesByPalleteMonetaryRatio,
  colorIndexesByPalletePhysical,
} from './chartColors';
import { getEnglishUnit } from './helpers';
import { getDimensionalityForExamplePage } from '../MarketAnalysis/helpers';

const getMarketingAndSalesTextData = (
  marketingAndSalesInfo: IMarketingAndSalesData,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
): IMagazineTextItem[][] => {
  const {
    salesPlan: {
      goals,
      affectFactors,
      performanceIndictors,
      salesTeamStructure,
      motivationSystem,
      detailedSalesChannel,
    },
    marketingPlan: {
      advertisingCampaigns,
      pricingStrategy,
      budget,
      toolsAndTechnologies,
      positioning,
      brandElements,
    },
  } = marketingAndSalesInfo;

  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    return [
      [
        getMagazineTextObject(
          isExamplePage ? 'Sales Goals. ' : t('pdfPages.mappings.marketingAndSales.salesGoals'),
          goals,
        ),
        getMagazineTextObject(
          isExamplePage
            ? 'Sales Cycle Factors. '
            : t('pdfPages.mappings.marketingAndSales.salesCycleFactors'),
          affectFactors,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage
            ? 'Sales Channels Overview. '
            : t('pdfPages.mappings.marketingAndSales.salesChannelsOverview'),
          detailedSalesChannel,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage
            ? 'Advertising and Promotion. '
            : t('pdfPages.mappings.marketingAndSales.advertisingAndPromotion'),
          advertisingCampaigns,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Positioning. ' : t('pdfPages.mappings.marketingAndSales.positioning'),
          positioning,
        ),
        getMagazineTextObject(
          isExamplePage
            ? 'Pricing Strategy. '
            : t('pdfPages.mappings.marketingAndSales.pricingStrategy'),
          pricingStrategy,
        ),
      ],
    ];
  }

  return [
    [
      getMagazineTextObject(
        isExamplePage ? 'Sales Goals. ' : t('pdfPages.mappings.marketingAndSales.salesGoals'),
        goals,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage
          ? 'Sales Cycle Factors. '
          : t('pdfPages.mappings.marketingAndSales.salesCycleFactors'),
        affectFactors,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Performance Indicators. '
          : t('pdfPages.mappings.marketingAndSales.performanceIndicators'),
        performanceIndictors,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage
          ? 'Sales Channels Overview. '
          : t('pdfPages.mappings.marketingAndSales.salesChannelsOverview'),
        detailedSalesChannel,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Sales Team. ' : t('pdfPages.mappings.marketingAndSales.salesTeam'),
        salesTeamStructure,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Compensation and Motivation System. '
          : t('pdfPages.mappings.marketingAndSales.compensationAndMotivationSystem'),
        motivationSystem,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage
          ? 'Advertising and Promotion. '
          : t('pdfPages.mappings.marketingAndSales.advertisingAndPromotion'),
        advertisingCampaigns,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Marketing and Advertising Budget. '
          : t('pdfPages.mappings.marketingAndSales.marketingAndAdvertisingBudget'),
        budget,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Branding and Identification. '
          : t('pdfPages.mappings.marketingAndSales.brandingAndIdentification'),
        brandElements,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Positioning. ' : t('pdfPages.mappings.marketingAndSales.positioning'),
        positioning,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Pricing Strategy. '
          : t('pdfPages.mappings.marketingAndSales.pricingStrategy'),
        pricingStrategy,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Customer Relationship Management. '
          : t('pdfPages.mappings.marketingAndSales.customerRelationshipManagement'),
        toolsAndTechnologies,
      ),
    ],
  ];
};

const getSalesChannelsData = (marketingAndSalesInfo: IMarketingAndSalesData) => {
  const salesChannels = marketingAndSalesInfo.salesPlan.salesChannels;

  if (!hasDataToRender(salesChannels)) {
    return [];
  }

  return marketingAndSalesInfo.salesPlan.salesChannels.map((channel, index) => ({
    ...channel,
    step: formatStepNumber(index + 1),
  }));
};

const getSalesFunnelData = (marketingAndSalesInfo: IMarketingAndSalesData) =>
  marketingAndSalesInfo.salesPlan.stages.filter(({ stage, description }) => stage || description);

const getProductDataForPeriod = (
  products: Record<string, string | number>[],
  periodParts: string[],
) => {
  const result: number[][] = [];
  products?.forEach((item, index) => {
    periodParts.forEach((period) => {
      const currentNumber = Number(item?.[period.toLowerCase()]);
      const roundedNumber = roundNumberToOneDecimal(currentNumber);
      if (result[index]) {
        result[index].push(roundedNumber);
      } else {
        result.push([roundedNumber]);
      }
    });
  });

  return result;
};

const roundNumberToOneDecimal = (value: number) => Math.round(value * 10) / 10;

export const getSalesPlanTableData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): ISalesPlanTableProps => {
  const { period, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const global = businessPlanChapters.GLOBAL;
  const marketingAndSalesInfo = businessPlanChapters.MARKETING_AND_SALES;
  const periodKeys = Object.keys(periodTableHeadings[period]);
  const headers = Object.values(periodTableHeadings[period]).map((item) => item.toString());

  const chosenPeriodProductData = marketingAndSalesInfo?.salesPlan?.plannedProductsVolume
    ?.filter((product) => product && period in product)
    ?.map((product) => product[period])
    ?.filter((item) => item && Object.values(item).some(Boolean));

  const monetarySalesPlanData = getProductDataForPeriod(chosenPeriodProductData || [], periodKeys);

  const businessOperationsInfo = businessPlanChapters.BUSINESS_OPERATIONS;
  const productsInPhysical = businessOperationsInfo.production.plannedProductsVolume
    ?.map((product) => product[period])
    .filter((item) => Object.values(item).some(Boolean));

  const physicalSalesPlanData = getProductDataForPeriod(productsInPhysical || [], periodKeys);

  const sellingPriceData = monetarySalesPlanData.map((item, index) =>
    item.map((value, i) => roundNumberToOneDecimal(value / physicalSalesPlanData[index]?.[i])),
  );

  const totalValuesRowData: number[] = [];

  const chosenPeriodTotalValuesData =
    marketingAndSalesInfo?.salesPlan?.totalPlannedProductsVolume?.[period];

  if (chosenPeriodProductData) {
    periodKeys.forEach((columnKey) => {
      const currentTotalValue = Number(chosenPeriodTotalValuesData?.[columnKey]);
      totalValuesRowData.push(currentTotalValue);
    });
  }

  const products = global.products || [];
  const filledProducts = products.filter(
    (item) =>
      Boolean(Number(item[PRODUCT_NAMES.PRICE])) &&
      Boolean(Number(item[PRODUCT_NAMES.MONTHLY_SALES_PLAN_PHYSICAL])),
  );
  const currencySymbol = global?.currency?.symbol;
  const unit = global.unit;

  const rows = filledProducts.map((product, index) => ({
    productName: product.name || (isExamplePage ? 'Product/Service' : t('global.products.name')),
    characteristics: [
      {
        title: `${isExamplePage ? 'In physical' : t('pdfPages.mappings.marketingAndSales.inPhysical')}, ${isExamplePage ? getEnglishUnit(product.volumeUnits) : t(`justUnits.${product.volumeUnits}`)} ${unit === DIMENSIONALITY_VALUES.SINGLE ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
        values: physicalSalesPlanData[index],
      },
      {
        title: `${isExamplePage ? 'Selling Price' : t('pdfPages.mappings.marketingAndSales.sellingPrice')}, ${currencySymbol}/${isExamplePage ? getEnglishUnit(product.volumeUnits) : t(`justUnits.${product.volumeUnits}`)}`,
        values: sellingPriceData[index],
      },
      {
        title: `${isExamplePage ? 'In monetary' : t('pdfPages.mappings.marketingAndSales.inMonetary')}, ${currencySymbol} ${unit === DIMENSIONALITY_VALUES.SINGLE ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
        values: monetarySalesPlanData[index],
      },
    ],
  }));

  return {
    columns: headers,
    rows,
    totalValues: totalValuesRowData,
    currencySymbol,
    unit,
    isExamplePage
  };
};

export const getSalesPlanInMonetaryChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithBars[] => {
  const { period, periodTableHeadings, products, currency, unit } = businessPlanChapters.GLOBAL;
  const marketingAndSalesInfo = businessPlanChapters.MARKETING_AND_SALES;
  const currencySymbol = currency?.symbol || '';
  const units = `${currencySymbol} ${isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`;

  const periodKeys = Object.keys(periodTableHeadings[period as IPeriodNames]);
  const labels = Object.values(periodTableHeadings[period as IPeriodNames]).map((item) =>
    item.toString(),
  );
  const filledProducts = products?.filter(
    (item) =>
      Boolean(Number(item[PRODUCT_NAMES.PRICE])) &&
      Boolean(Number(item[PRODUCT_NAMES.MONTHLY_SALES_PLAN_PHYSICAL])),
  );
  const datasetsLabels =
    filledProducts?.map(
      (product) => product.name || (isExamplePage ? 'Product/Service' : t('global.products.name')),
    ) || [];
  const MAX_PRODUCTS_PER_CHART = 3;
  const plannedProductsVolumeEmpty =
    marketingAndSalesInfo?.salesPlan?.plannedProductsVolume?.length === 0;

  const firstChartProductData = !plannedProductsVolumeEmpty
    ? marketingAndSalesInfo?.salesPlan?.plannedProductsVolume
        ?.filter((item) => item?.quarterValues && Object.values(item.quarterValues).some(Boolean))
        ?.slice(0, MAX_PRODUCTS_PER_CHART)
        .map((product) => product[period])
    : [];

  const secondChartProductData = !plannedProductsVolumeEmpty
    ? marketingAndSalesInfo?.salesPlan?.plannedProductsVolume
        ?.filter((item) => item?.quarterValues && Object.values(item?.quarterValues).some(Boolean))
        ?.slice(MAX_PRODUCTS_PER_CHART)
        .map((product) => product[period])
    : [];

  const firstChartResult = getProductDataForPeriod(firstChartProductData || [], periodKeys);
  const secondChartResult = getProductDataForPeriod(secondChartProductData || [], periodKeys);

  const type = getColorType();
  const colors = getColorStyles(colorIndexesByPalleteMonetary[type]);

  return [
    {
      title: '',
      datasetsLabels,
      data: firstChartResult,
      unit: units,
      labels,
      colors,
    },
    {
      title: '',
      datasetsLabels: datasetsLabels.slice(MAX_PRODUCTS_PER_CHART),
      data: secondChartResult,
      unit: units,
      labels,
      colors: colors.slice(MAX_PRODUCTS_PER_CHART),
    },
  ];
};

export const getRatioInMonetaryChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithStackedBars => {
  const { products, period, unit, currency, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const { salesPlan } = businessPlanChapters.MARKETING_AND_SALES;
  const productNames =
    products?.map(
      (product) => product.name ?? (isExamplePage ? 'Product/Service' : t('global.products.name')),
    ) || [];
  const chosenPeriodProductData =
    salesPlan?.plannedProductsVolume?.map((product) => product?.[period]) || [];
  const chosenPeriodTotalValuesData = salesPlan?.totalPlannedProductsVolume?.[period] || {};
  const symbol = currency?.symbol || '';

  const datasets = productNames.reduce<IChartDatasetItem[]>((acc, variant, index) => {
    if (
      index in chosenPeriodProductData &&
      !Object.values(chosenPeriodProductData[index] || {}).some(Boolean)
    ) {
      return acc;
    }

    const currentProductValues =
      chosenPeriodProductData.length > 0 && chosenPeriodProductData[index]
        ? Object.values(chosenPeriodProductData[index] || {}).map(Number)
        : [];

    const currentProductTotalValues = chosenPeriodTotalValuesData
      ? Object.values(chosenPeriodTotalValuesData || {}).map(Number)
      : [];

    const monetaryRatios = currentProductValues.reduce<number[]>((acc, num, index) => {
      const monetaryRatio = Number(((num / currentProductTotalValues[index]) * 100).toFixed(2));

      return [...acc, monetaryRatio];
    }, []);

    return [...acc, { label: variant, data: monetaryRatios }];
  }, []);

  const type = getColorType();
  const colors = getColorStyles(colorIndexesByPalleteMonetaryRatio[type]);
  const labels = Object.values(periodTableHeadings[period as IPeriodNames]).map((item) =>
    item.toString(),
  );

  return {
    title: '',
    unit: `${symbol} ${isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    datasets,
    totals: [],
    labels,
    colors,
  };
};

const colorIndexesTotalMonetaryValues = {
  green: [16],
  blue: [15],
  red: [9],
  blueGreen: [9],
  blueRed: [0],
  blueYellow: [0],
};

export const getTotalValuesInMonetaryChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
) => {
  const type = getColorType();
  const colors = getColorStyles(colorIndexesTotalMonetaryValues[type]);

  const { period, unit, currency, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const marketingAndSalesInfo = businessPlanChapters.MARKETING_AND_SALES;
  const currencySymbol = currency?.symbol || '';
  const units = `${currencySymbol} ${isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`;

  const chosenPeriodProductData = marketingAndSalesInfo?.salesPlan?.plannedProductsVolume
    ?.filter((product) => product && period in product)
    ?.map((product) => product[period]);

  const chosenPeriodTotalValuesData =
    marketingAndSalesInfo?.salesPlan?.totalPlannedProductsVolume?.[period];
  const periodKeys = Object.keys(periodTableHeadings[period as IPeriodNames]);
  const labels = Object.values(periodTableHeadings[period as IPeriodNames]).map((item) =>
    item.toString(),
  );

  let result: number[][] = [];

  if (chosenPeriodProductData) {
    periodKeys.forEach((periodKey, index) => {
      const currentTotalValue = Number(chosenPeriodTotalValuesData?.[periodKey]);
      if (index === 0) {
        result = [[currentTotalValue]];
      } else {
        result[0].push(currentTotalValue);
      }
    });
  }

  return {
    title: '',
    unit: units,
    data: result,
    labels,
    datasetsLabels: [
      isExamplePage ? 'Sales, total' : t('pdfPages.mappings.marketingAndSales.salesTotal'),
    ],
    colors,
  };
};

export const getValuesInPhysicalChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
) => {
  const {
    unit,
    products: globalProducts,
    period: globalPeriod,
    currency,
    periodTableHeadings,
  } = businessPlanChapters.GLOBAL;
  const businessOperationsInfo = businessPlanChapters.BUSINESS_OPERATIONS;
  const currencySymbol = currency?.symbol || '';
  const type = getColorType();
  const colors = getColorStyles(colorIndexesByPalletePhysical[type]);

  const period = globalPeriod as IPeriodNames;
  const products = businessOperationsInfo.production.plannedProductsVolume;
  const filledProducts = globalProducts?.filter(
    (item) =>
      Boolean(Number(item[PRODUCT_NAMES.PRICE])) &&
      Boolean(Number(item[PRODUCT_NAMES.MONTHLY_SALES_PLAN_PHYSICAL])),
  );
  const datasetsLabels =
    filledProducts?.map(
      (product) => product.name || (isExamplePage ? 'Product/Service' : t('global.products.name')),
    ) || [];

  const chosenPeriodProductData = products
    ?.map((product) => product[period])
    .filter((item) => Object.values(item).some(Boolean));
  const periodKeys = Object.keys(periodTableHeadings[period]);
  const labels = Object.values(periodTableHeadings[period]).map((item) => item.toString());
  const data = getProductDataForPeriod(chosenPeriodProductData || [], periodKeys);

  return {
    title: '',
    datasetsLabels,
    data,
    labels,
    colors,
    unit: `${currencySymbol} ${t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
  };
};

export const getMarketingAndSalesProps = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
) => {
  const marketingAndSalesInfo = businessPlanChapters.MARKETING_AND_SALES;

  return {
    magazineTexts: getMarketingAndSalesTextData(
      marketingAndSalesInfo,
      businessPlanType,
      isExamplePage,
    ) as MagazineTextItemType[][],
    salesChannelsData: getSalesChannelsData(marketingAndSalesInfo),
    salesFunnelData: getSalesFunnelData(marketingAndSalesInfo),
  };
};
